import isNil from 'lodash/isNil';

import { BankAccount, Contact, CustomValue, Integration, isNilOrEmpty } from '../..';

export const INTEGRATION_CONFIG_EXACT_ONLINE_ACCESS_TOKEN = 'accessToken';
export const INTEGRATION_CONFIG_EXACT_ONLINE_REFRESH_TOKEN = 'refreshToken';
export const INTEGRATION_CONFIG_EXACT_ONLINE_ACCESS_TOKEN_EXPIRES_AT = 'accessTokenExpiresAt';
export const INTEGRATION_CONFIG_EXACT_ONLINE_ACCOUNTS_TIMESTAMP = 'accountsTimestamp';

export enum EXACT_ONLINE_TIMESTAMP_CONFIGS {
  ACCOUNTS = INTEGRATION_CONFIG_EXACT_ONLINE_ACCOUNTS_TIMESTAMP,
}

export const exactOnlineDefaultConfigurations = {
  [INTEGRATION_CONFIG_EXACT_ONLINE_ACCESS_TOKEN]: '',
  [INTEGRATION_CONFIG_EXACT_ONLINE_REFRESH_TOKEN]: '',
};

export const EXACT_ONLINE_ID_NAME_FOR_CUSTOM_VALUE = 'exactOnlineId';
export const EXACT_ONLINE_CODE_FOR_CUSTOM_VALUE = 'exactOnlineCode';
export const EXACT_ONLINE_DISPLAY_CODE_FOR_CUSTOM_VALUE = 'exactOnlineDisplayCode';
export const EXACT_ONLINE_IDS_NAME_FOR_CUSTOM_VALUE = 'exactOnlineIds';
export const EXACT_ONLINE_JOURNAL_CODE_NAME_FOR_CUSTOM_VALUE = 'exactOnlineJournalCode';
export const EXACT_ONLINE_CLASS_NAME_FOR_CUSTOM_VALUE = 'EXACT_ONLINE_CLASS';
export const EXACT_ONLINE_EOJOURNAL_CODE_FOR_INVOICE = 'EOJOURNAL_CODE_FOR_INVOICE';
export const EXACT_ONLINE_EOJOURNAL_CODE_FOR_CREDIT_NOTE = 'EOJOURNAL_CODE_FOR_CREDIT_NOTE';
export const EXACT_ONLINE_EOJOURNAL_CODE_FOR_CHARGES = 'EOJOURNAL_CODE_FOR_CHARGES';
export const EXACT_ONLINE_GL_ACCOUNTS_MATCHING = 'EXACT_ONLINE_GL_ACCOUNTS_MATCHING';
export const EXACT_ONLINE_IS_SYNC = 'exactOnlineSync';

export const EXACT_ONLINE_BANK_ACCOUNTS_SYNC_LAST_RUN_FOR_CUSTOM_VALUE = 'exactOnlineBankAccountsSyncLastRun';
export const EXACT_ONLINE_CHARGES_SYNC_LAST_RUN_FOR_CUSTOM_VALUE = 'exactOnlineChargesSyncLastRun';
export const EXACT_ONLINE_CONTACTS_SYNC_LAST_RUN_FOR_CUSTOM_VALUE = 'exactOnlineContactsSyncLastRun';
export const EXACT_ONLINE_INVOICES_SYNC_LAST_RUN_FOR_CUSTOM_VALUE = 'exactOnlineInvoicesSyncLastRun';
// Used for transactions updates that are pushed in Exact Online
export const EXACT_ONLINE_TRANSACTIONS_SYNC_LAST_RUN_FOR_CUSTOM_VALUE = 'exactOnlineTransactionsSyncLastRun';
// Used for transactions we get from Exact Online
export const EXACT_ONLINE_TRANSACTIONS_GET_LAST_RUN_FOR_CUSTOM_VALUE = 'exactOnlineTransactionsGetLastRun';

export const EXACT_ONLINE_UNKNOWN_ACCOUNT_MATCHING_FOR_CUSTOM_VALUE = 'exactOnlineUnknownAccount';

export const EXACT_ONLINE_TRANSACTIONS_SYNC_METHOD_FOR_CUSTOM_VALUE = 'exactOnlineTransactionsMethod';
export const EXACT_ONLINE_TRANSACTIONS_SYNC_FROM_U2R_TO_EO = 'fromU2RToEO';
export const EXACT_ONLINE_TRANSACTIONS_SYNC_FROM_EO_TO_U2R = 'fromEOToU2R';

export const EXACT_ONLINE_CONTACTS_SYNC_METHOD_FOR_CUSTOM_VALUE = 'exactOnlineContactsSyncMethod';
export const EXACT_ONLINE_CONTACTS_SYNC_BOTH_WAYS = 'bothWays';
export const EXACT_ONLINE_CONTACTS_SYNC_NOT_UPDATE_EO = 'notUpdateEO';

const EXACT_ONLINE_ALL_CUSTOM_VALUE_NAMES = [
  EXACT_ONLINE_ID_NAME_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_CODE_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_DISPLAY_CODE_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_IDS_NAME_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_JOURNAL_CODE_NAME_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_CLASS_NAME_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_EOJOURNAL_CODE_FOR_INVOICE,
  EXACT_ONLINE_EOJOURNAL_CODE_FOR_CREDIT_NOTE,
  EXACT_ONLINE_EOJOURNAL_CODE_FOR_CHARGES,
  EXACT_ONLINE_GL_ACCOUNTS_MATCHING,
  EXACT_ONLINE_IS_SYNC,
  EXACT_ONLINE_BANK_ACCOUNTS_SYNC_LAST_RUN_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_CHARGES_SYNC_LAST_RUN_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_CONTACTS_SYNC_LAST_RUN_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_INVOICES_SYNC_LAST_RUN_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_TRANSACTIONS_SYNC_LAST_RUN_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_TRANSACTIONS_GET_LAST_RUN_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_UNKNOWN_ACCOUNT_MATCHING_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_TRANSACTIONS_SYNC_METHOD_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_CONTACTS_SYNC_METHOD_FOR_CUSTOM_VALUE,
];

export type ExactOnlineTransactionsSync =
  | typeof EXACT_ONLINE_TRANSACTIONS_SYNC_FROM_U2R_TO_EO
  | typeof EXACT_ONLINE_TRANSACTIONS_SYNC_FROM_EO_TO_U2R;

export type ExactOnlineContactsSync =
  | typeof EXACT_ONLINE_CONTACTS_SYNC_BOTH_WAYS
  | typeof EXACT_ONLINE_CONTACTS_SYNC_NOT_UPDATE_EO;

export type ExactOnlineSyncLastRun =
  | typeof EXACT_ONLINE_BANK_ACCOUNTS_SYNC_LAST_RUN_FOR_CUSTOM_VALUE
  | typeof EXACT_ONLINE_CHARGES_SYNC_LAST_RUN_FOR_CUSTOM_VALUE
  | typeof EXACT_ONLINE_CONTACTS_SYNC_LAST_RUN_FOR_CUSTOM_VALUE
  | typeof EXACT_ONLINE_INVOICES_SYNC_LAST_RUN_FOR_CUSTOM_VALUE
  | typeof EXACT_ONLINE_TRANSACTIONS_SYNC_LAST_RUN_FOR_CUSTOM_VALUE;

export const getExactOnlineSyncCustomValue = () => ({
  name: EXACT_ONLINE_IS_SYNC,
  value: 'true',
});

export const getExactOnlineDivision = (customValues: CustomValue[] | undefined | null) =>
  getExactOnlineIdWithDivision(customValues)?.division;

export const getExactOnlineCode = (customValues: CustomValue[] | undefined | null) => {
  if (isNilOrEmpty(customValues)) {
    return;
  }
  return customValues.find((customValue) => customValue.name === EXACT_ONLINE_CODE_FOR_CUSTOM_VALUE)?.value;
};

export const getExactOnlineId = (customValues: CustomValue[] | undefined | null) =>
  getExactOnlineIdWithDivision(customValues)?.id;

export const isSyncWithExactOnline = (customValues: CustomValue[] | undefined | null) =>
  !isNil(getExactOnlineId(customValues)) ||
  !isNil(getExactOnlineIds(customValues)) ||
  !isNil(getExactOnlineCode(customValues)) ||
  (customValues ?? []).some((customValue) => customValue.name === EXACT_ONLINE_IS_SYNC && customValue.value === 'true');

export const getExactOnlineIdForDivision = (
  customValues: CustomValue[] | undefined | null,
  division: string
): string | undefined => {
  const eoValues = getExactOnlineIdsWithDivision(customValues);
  return eoValues?.find((eoValue) => eoValue.division === division)?.id;
};

export const isClientContactLinkableAsSupplier = (
  clientContact: Pick<Contact, 'customValues'> | null | undefined,
  bankAccount?: Pick<BankAccount, 'customValues'> | null,
  exactOnlineIntegration?: Integration | null
): boolean => {
  if (isNil(clientContact)) {
    return false;
  }
  if (isNil(exactOnlineIntegration)) {
    return true;
  }
  const exactOnlineDivision = getExactOnlineDivision(bankAccount?.customValues);
  return (
    !isNil(exactOnlineDivision) && !isNil(getExactOnlineIdForDivision(clientContact.customValues, exactOnlineDivision))
  );
};

export const getExactOnlineIds = (customValues: CustomValue[] | undefined | null): string[] | undefined => {
  const eoValues = getExactOnlineIdsWithDivision(customValues);
  return eoValues?.map((eoValue) => eoValue.id);
};

export const getExactOnlineIdsWithDivision = (
  customValues: CustomValue[] | undefined | null
): { division: string; id: string }[] | undefined => {
  if (isNilOrEmpty(customValues)) {
    return;
  }
  const value = customValues.find((customValue) => customValue.name === EXACT_ONLINE_IDS_NAME_FOR_CUSTOM_VALUE)?.value;
  if (isNil(value)) {
    return;
  }

  const eoValues = JSON.parse(value) as { division: string; id: string }[];
  return eoValues;
};

export const getExactOnlineIdWithDivision = (
  customValues: CustomValue[] | undefined | null
): { division: string; id: string } | undefined => {
  if (isNilOrEmpty(customValues)) {
    return;
  }
  const value = customValues.find((customValue) => customValue.name === EXACT_ONLINE_ID_NAME_FOR_CUSTOM_VALUE)?.value;
  if (isNil(value)) {
    return;
  }

  const eoValue = JSON.parse(value) as { division: string; id: string };
  return eoValue;
};

export const getExactOnlineDivisionsSynchronized = (customValues: CustomValue[] | undefined | null) => {
  const exactOnlineIdsWithDivision = getExactOnlineIdsWithDivision(customValues);
  return (
    exactOnlineIdsWithDivision?.reduce<string[]>((acc, { division }) => {
      if (!acc.includes(division)) {
        acc.push(division);
      }
      return acc;
    }, []) ?? []
  );
};

export const getExactOnlineIdsFromGenericList = <T extends { customValues?: CustomValue[] | undefined | null }>(
  genericList: T[]
) =>
  genericList.reduce((acc, item) => {
    const exactOnlineIds = getExactOnlineIds(item.customValues);
    if (exactOnlineIds) {
      acc.push(...exactOnlineIds);
      return acc;
    }
    const exactOnlineId = getExactOnlineId(item.customValues);
    if (exactOnlineId) {
      acc.push(exactOnlineId);
    }
    return acc;
  }, [] as string[]);

export const eraseCustomValuesLinkedToExactOnline = (customValues: CustomValue[] | null | undefined): CustomValue[] =>
  (customValues ?? []).filter((customValue) => !EXACT_ONLINE_ALL_CUSTOM_VALUE_NAMES.includes(customValue.name));
